/**
 * Color Presets
 *
 * Centralized palette constants for:
 *   - Marker color preset swatches used by the drawing tool custom color panel.
 *   - Road network colors (motorway / expressway / DK / other) used as MapLibre
 *     paint fallbacks in `mapColorUtils.ts`. MapLibre paint values are not able
 *     to consume CSS custom properties at runtime, so these hex literals live
 *     here (whitelisted by F-N3 via `src/constants/`).
 *   - Symbol layer colors (cluster fills, cluster text/stroke, marker border)
 *     used by `UnifiedAnchorSymbolLayer/styles/symbolLayerStyles.ts`.
 *
 * Rule F-N3: hex literals are only allowed under `src/constants/`, `src/global.css`
 * and `src/main.tsx` (Mantine palette). All other files must reference these
 * presets or CSS variables.
 *
 * @module constants/colorPresets
 */

// ===== MARKER COLOR PRESETS =====

/**
 * Shared marker color palette used by:
 *   - `CustomColorPanel` (drawing tools color picker swatches)
 *
 * Ordered for typical UX priority (primary blue first, then warm alerts,
 * cool supporting colors, then neutrals).
 */
export const MARKER_COLOR_PRESETS = [
  '#3b82f6', // blue — default / primary
  '#ef4444', // red — alerts / warnings
  '#10b981', // emerald — success / complete
  '#f59e0b', // amber — caution / attention
  '#8b5cf6', // violet — special / priority
  '#ec4899', // pink — highlight
  '#06b6d4', // cyan — info / water
  '#84cc16', // lime — accent
  '#f97316', // orange — construction / maintenance
  '#6b7280', // gray — neutral
  '#1f2937', // slate — high contrast
] as const;

export type MarkerColorPreset = (typeof MARKER_COLOR_PRESETS)[number];

// ===== DRAWING TOOLS SWATCHES =====

/**
 * Rainbow swatch set exposed by the drawing-tools custom color panel.
 * This is a "pick-any-color" shortcut grid, distinct from the semantic
 * marker preset palette above.
 */
export const DRAWING_COLOR_SWATCHES = [
  '#ef4444', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#06b6d4', // cyan
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#d946ef', // fuchsia
  '#64748b', // gray
  '#000000', // black
] as const;

/**
 * Per-target default colors used by the drawing tools color panel as
 * fallback when the active geometry does not have an explicit color yet.
 */
export const DRAWING_DEFAULT_COLORS = {
  marker: '#3b82f6',
  line: '#ef4444',
  shapeFill: '#22c55e',
  shapeBorder: '#22c55e',
} as const;

// ===== ROAD COLOR PALETTE =====

/**
 * Fallback colors for Polish road network styling.
 *
 * These back the CSS variables `--road-*-color` but are also used as
 * runtime fallbacks when CSS variables cannot be read (MapLibre paint
 * expressions, offscreen sprite generation, SSR-safe initialization).
 *
 * Roles mirror the CSS variable groups consumed by `mapColorUtils.ts`.
 */
/** Uniform alert red used for all national-road hover variants. */
const NATIONAL_HOVER_RED = '#EF4444';

export const ROAD_COLOR_PALETTE = {
  /** Base palette for national roads (A / S / DK / other). */
  national: {
    motorway: '#92400E', // dark brown — A1, A2, A4, ...
    expressway: '#92400E', // dark amber — S roads
    nationalDk: '#B45309', // light amber — DK national roads
    other: '#CA8A04', // golden amber — other national
  },
  /** Hover variants for national roads (uniform alert red). */
  nationalHover: {
    motorway: NATIONAL_HOVER_RED,
    expressway: NATIONAL_HOVER_RED,
    nationalDk: NATIONAL_HOVER_RED,
    other: NATIONAL_HOVER_RED,
  },
  /** Voivodeship (regional) roads. */
  voivodeship: {
    primary: '#D97706', // orange-amber — primary regional
    secondary: '#EAB308', // yellow — secondary / tertiary
    hover: '#F97316', // orange — hover
  },
} as const;

export type RoadColorPalette = typeof ROAD_COLOR_PALETTE;

// ===== SYMBOL LAYER COLORS =====

/**
 * Hex colors used by MapLibre symbol/circle layers in
 * `UnifiedAnchorSymbolLayer/styles/symbolLayerStyles.ts`.
 *
 * MapLibre paint expressions require literal color strings — they cannot
 * read CSS custom properties — so these live in a constants module to
 * satisfy F-N3.
 */
export const SYMBOL_LAYER_COLORS = {
  /** Marker border colors (stroke around the marker shape). */
  border: {
    light: '#ffffff', // white outline on light basemap
    dark: '#1a1b1e', // Mantine `dark.7` — outline on dark basemap
  },
  /** Cluster circle fill steps — darker shade = larger cluster. */
  cluster: {
    /** Indigo ramp used on light basemaps (< 10 / 10–49 / 50+). */
    light: {
      small: '#748FFC', // indigo-4
      medium: '#4C6EF5', // indigo-6
      large: '#3B5BDB', // indigo-8
    },
    /** Amber ramp used on dark basemaps (< 10 / 10–49 / 50+). */
    dark: {
      small: '#fcd34d', // amber-300
      medium: '#f59e0b', // amber-500
      large: '#d97706', // amber-600
    },
    /** Cluster stroke color on light basemap. */
    strokeLight: '#ffffff',
    /** Cluster count text color on light basemap. */
    textLight: '#ffffff',
  },
} as const;

export type SymbolLayerColors = typeof SYMBOL_LAYER_COLORS;
